import {
	SuperGridPluginParams, useSuperGridValidate, SuperGridValidationEntity,
} from '@aleph/supergrid';
import { useApolloClient } from '@apollo/client';
import {
	GraphQLErrorWithFormValidation,
} from 'wb-reddit/app/routes/superGrid/utils';
import { LEVEL_ID_FILTER } from 'wb-spotify/app/routes/superGrid/actionRow/filters/utils';
import { UpdateCreativeInput } from 'wb-reddit/graphql/generated';
import {
	debounce, merge,
} from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import {
	alephToast,
	NotificationToast,
	NotificationType,
	UndoIcon,
} from '@aleph/shared-components';
import { transformSpotifyRows } from 'wb-spotify/app/routes/superGrid/config';
import { captureException } from '@sentry/react';
import { validateSpotifyEntity } from 'wb-spotify/app/routes/superGrid/validation/utils';
import { CellValueChangedEvent } from 'ag-grid-community';
import { SpotifyLevel, mapValidationErrors } from 'wb-spotify/app/routes/superGrid/utils';
import {
	GroupTargetsGender,
	GroupTargetsSensitiveTopicExclusionsFilterOption,
	UpdateAdInput,
	UpdateCampaignInput,
	UpdateEntitiesDocument,
	UpdateGroupInput,
} from 'wb-spotify/graphql/generated';
import { SpotifySupergridGroupData } from 'wb-spotify/app/routes/superGrid/validation/frontendGroupSchema';
import { type RecursiveNonNullableExtractor } from 'wb-spotify/app/common/utils';
import { prepareSupergridGroupTargetsForBackend } from 'wb-spotify/app/routes/superGrid/mutator/hooks/utils';

type GetFieldType<KeyPath extends (keyof any)[]> = RecursiveNonNullableExtractor<SpotifySupergridGroupData, KeyPath>;

const UPDATE_FIELD_MAPS = {
	name:
		(name: GetFieldType<['name']>, level: SpotifyLevel) => ({ [`${level}Name`]: name }),
	category:
		(category: GetFieldType<['category']>) => ({ categoryId: category.value }),
	'groupTargets.groupTargetsArtists': (
		artists: GetFieldType<['groupTargets', 'groupTargetsArtists']>,
		_level: SpotifyLevel,
		data: any,
	) => ({
		groupTargets: {
			...prepareSupergridGroupTargetsForBackend(data),
			groupTargetsArtistIds: artists?.map(artist => artist.value) ?? [],
		},
	}),
	'groupTargets.groupTargetsGeoTargets.countryCode': (
		countryCode: GetFieldType<['groupTargets', 'groupTargetsGeoTargets', 'countryCode']>,
		_level: SpotifyLevel,
		data: any,
	) => {
		const prepared = prepareSupergridGroupTargetsForBackend(data);

		return {
			groupTargets: {
				...prepared,
				groupTargetsGeoTargets: {
					...prepared.groupTargetsGeoTargets,
					countryCode: countryCode?.value as string,
				},
			},
		};
	},
	'groupTargets.groupTargetsGeoTargets.cityIds': (
		cityIds: GetFieldType<['groupTargets', 'groupTargetsGeoTargets', 'cityIds']>,
		_level: SpotifyLevel,
		data: any,
	) => {
		const prepared = prepareSupergridGroupTargetsForBackend(data);

		return {
			groupTargets: {
				...prepared,
				groupTargetsGeoTargets: {
					...prepared.groupTargetsGeoTargets,
					cityIds: cityIds?.map(city => city.value) ?? [],
				},
			},
		};
	},
	'groupTargets.groupTargetsGeoTargets.dmaIds': (
		dmaIds: GetFieldType<['groupTargets', 'groupTargetsGeoTargets', 'dmaIds']>,
		_level: SpotifyLevel,
		data: any,
	) => {
		const prepared = prepareSupergridGroupTargetsForBackend(data);

		return {
			groupTargets: {
				...prepared,
				groupTargetsGeoTargets: {
					...prepared.groupTargetsGeoTargets,
					dmaIds: dmaIds?.map(dma => dma.value) ?? [],
				},
			},
		};
	},
	'groupTargets.groupTargetsGeoTargets.regionIds': (
		regionIds: GetFieldType<['groupTargets', 'groupTargetsGeoTargets', 'regionIds']>,
		_level: SpotifyLevel,
		data: any,
	) => {
		const prepared = prepareSupergridGroupTargetsForBackend(data);

		return {
			groupTargets: {
				...prepared,
				groupTargetsGeoTargets: {
					...prepared.groupTargetsGeoTargets,
					regionIds: regionIds?.map(region => region.value) ?? [],
				},
			},
		};
	},
	'groupTargets.groupTargetsGeoTargets.postalCodeIds': (
		postalCodeIds: GetFieldType<['groupTargets', 'groupTargetsGeoTargets', 'postalCodeIds']>,
		_level: SpotifyLevel,
		data: any,
	) => {
		const prepared = prepareSupergridGroupTargetsForBackend(data);

		return {
			groupTargets: {
				...prepared,
				groupTargetsGeoTargets: {
					...prepared.groupTargetsGeoTargets,
					postalCodeIds: postalCodeIds?.map(postalCode => postalCode.value) ?? [],
				},
			},
		};
	},
	'groupTargets.groupTargetsPlacements': (
		placements: GetFieldType<['groupTargets', 'groupTargetsPlacements']>,
		_level: SpotifyLevel,
		data: any,
	) => ({
		groupTargets: {
			...prepareSupergridGroupTargetsForBackend(data),
			groupTargetsPlacements: placements?.map(placement => placement.value) ?? [],
		},
	}),
	'groupTargets.groupTargetsPodcastEpisodeTopics': (
		topics: GetFieldType<['groupTargets', 'groupTargetsPodcastEpisodeTopics']>,
		_level: SpotifyLevel,
		data: any,
	) => ({
		groupTargets: {
			...prepareSupergridGroupTargetsForBackend(data),
			groupTargetsPodcastEpisodeTopicIds: topics?.map(topic => topic.value) ?? [],
		},
	}),
	'groupTargets.groupTargetsAgeRanges': (
		ageRanges: GetFieldType<['groupTargets', 'groupTargetsAgeRanges']>,
		_level: SpotifyLevel,
		data: any,
	) => ({
		groupTargets: {
			...prepareSupergridGroupTargetsForBackend(data),
			groupTargetsAgeRanges: ageRanges?.map(ageRange => ({
				min: Number(ageRange.value.split('-')[0]),
				max: Number(ageRange.value.split('-')[1]),
			})) ?? [],
		},
	}),
	'groupTargets.groupTargetsGenders': (
		gender: GetFieldType<['groupTargets', 'groupTargetsGenders']>,
		_level: SpotifyLevel,
		data: any,
	) => ({
		groupTargets: {
			...prepareSupergridGroupTargetsForBackend(data),
			groupTargetsGenders: gender ? [gender as GroupTargetsGender] : undefined,
		},
	}),
	'groupTargets.groupTargetsInterests': (
		interests: GetFieldType<['groupTargets', 'groupTargetsInterests']>,
		_level: SpotifyLevel,
		data: any,
	) => ({
		groupTargets: {
			...prepareSupergridGroupTargetsForBackend(data),
			groupTargetsInterestIds: interests?.map(interest => interest.value) ?? [],
		},
	}),
	'groupTargets.groupTargetsLanguage.language': (
		language: GetFieldType<['groupTargets', 'groupTargetsLanguage', 'language']>,
		_level: SpotifyLevel,
		data: any,
	) => ({
		groupTargets: {
			...prepareSupergridGroupTargetsForBackend(data),
			groupTargetsLanguage: language?.value,
		},
	}),
	'groupTargets.groupTargetsPlatforms': (
		platforms: GetFieldType<['groupTargets', 'groupTargetsPlatforms']>,
		_level: SpotifyLevel,
		data: any,
	) => ({
		groupTargets: {
			...prepareSupergridGroupTargetsForBackend(data),
			groupTargetsPlatforms: platforms?.map(platform => platform.value) ?? [],
		},
	}),
	'groupTargets.groupTargetsGenres': (
		genres: GetFieldType<['groupTargets', 'groupTargetsGenres']>,
		_level: SpotifyLevel,
		data: any,
	) => ({
		groupTargets: {
			...prepareSupergridGroupTargetsForBackend(data),
			groupTargetsGenreIds: genres?.map(genre => genre.value) ?? [],
		},
	}),
	'groupTargets.groupTargetsPlaylists': (
		playlists: GetFieldType<['groupTargets', 'groupTargetsPlaylists']>,
		_level: SpotifyLevel,
		data: any,
	) => ({
		groupTargets: {
			...prepareSupergridGroupTargetsForBackend(data),
			groupTargetsPlaylistIds: playlists?.map(playlist => playlist.value) ?? [],
		},
	}),
	'groupTargets.groupTargetsSensitiveTopicExclusions.sensitiveTopics': (
		sensitiveTopics: GetFieldType<['groupTargets', 'groupTargetsSensitiveTopicExclusions', 'sensitiveTopics']>,
		_level: SpotifyLevel,
		data: any,
	) => ({
		groupTargets: {
			...prepareSupergridGroupTargetsForBackend(data),
			groupTargetsSensitiveTopicExclusions: {
				topics: sensitiveTopics.map((topic) => ({
					id:           topic.value as string,
					filterOption: GroupTargetsSensitiveTopicExclusionsFilterOption.Standard,
				})) ?? [],
			},
		},
	}),
	'groupBudget.microAmount': (
		microAmount: GetFieldType<['groupBudget', 'microAmount']>,
		level,
		data,
	) => ({
		groupBudget: {
			microAmount,
			type: data.groupBudget?.type,
		},
	}),
	'groupBudget.type': (
		type: GetFieldType<['groupBudget', 'type']>,
		level,
		data,
	) => ({
		groupBudget: {
			type,
			microAmount: data.groupBudget?.microAmount,
		},
	}),
	groupFrequencyCapPerWeek: (
		_changes,
		_level,
		data,
	) => ({
		groupFrequencyCaps: data.groupFrequencyCaps.map(({ __typename, ...cap }) => cap),
	}),
	groupFrequencyCapPerMonth: (
		_changes,
		_level,
		data,
	) => ({
		groupFrequencyCaps: data.groupFrequencyCaps.map(({ __typename, ...cap }) => cap),
	}),
	groupFrequencyCapPerDay: (
		_changes,
		_level,
		data,
	) => ({
		groupFrequencyCaps: data.groupFrequencyCaps.map(({ __typename, ...cap }) => cap),
	}),
	adAsset: (
		adAsset,
		_level,
		data,
	) => ({
		adAssets: {
			assetId:          adAsset.id,
			companionAssetId: data.adCompanionAsset?.id,
			logoAssetId:      data.adLogoAsset?.id,
		},
	}),
	adCompanionAsset: (
		adCompanionAsset,
		_level,
		data,
	) => ({
		adAssets: {
			assetId:          data.adAsset?.id,
			companionAssetId: adCompanionAsset.id,
			logoAssetId:      data.adLogoAsset?.id,
		},
	}),
	adLogoAsset: (
		adLogoAsset,
		_level,
		data,
	) => ({
		adAssets: {
			assetId:          data.adAsset?.id,
			companionAssetId: data.adCompanionAsset?.id,
			logoAssetId:      adLogoAsset?.id,
		},
	}),
	adCanvasAsset: (
		adCanvasAsset,
		_level,
		data,
	) => ({
		adAssets: {
			assetId:          data.adAsset?.id,
			companionAssetId: data.adCompanionAsset?.id,
			logoAssetId:      data.adLogoAsset?.id,
			canvasAssetId:    adCanvasAsset?.id,
		},
	}),

};

const getUpdateMutationVariables = (changes: Record<string, any>, level: SpotifyLevel, data: Record<string, any>) => Object.keys(changes).reduce((accu, field) => {
	console.log('getUpdateMutationVariables field', field);

	if (UPDATE_FIELD_MAPS[field]) {
		return {
			...accu,
			...UPDATE_FIELD_MAPS[field](changes[field], level, data, accu),
		};
	}

	accu[field] = changes[field];

	return accu;
}, {});

const getSuccessfulEvents = (events: CellValueChangedEvent[], data: Record<string, any>[]) => data.reduce(
	(accu, entity) => {
		const affectedEvents = events.filter(e => e.data?.id === entity?.id);

		if (affectedEvents?.length > 0) {
			affectedEvents.forEach(event => {
				accu.push({
					...event,
					data: entity,
				});
			});
		}

		return accu;
	},
	[],
);

const getAndMapGeneralErrors = (errors: readonly GraphQLErrorWithFormValidation[], level?: SpotifyLevel) => errors?.filter(error => !error?.form)?.map(
	error => ({
		level,
		id:      `general_${error?.hash ?? performance.now()}`,
		field:   'generalError',
		message: error?.debugMessage ?? error?.message ?? 'Unknown Error',
	}),
) ?? [];

const mergeVariablesByIds = <T extends UpdateCampaignInput | UpdateGroupInput | UpdateAdInput | UpdateCreativeInput>(variables: Array<T & { id: string }>): Array<T> => {
	const variablesById = {};

	variables.forEach(variable => {
		variablesById[variable.id] = merge(
			variablesById[variable.id] ?? {},
			{
				...variable,
				id: undefined,
			},
		);
	});

	return Object.values(variablesById);
};

const useSpotifyEditPlugin = ({ gridApi }: SuperGridPluginParams) => {
	const client = useApolloClient();
	const { context: { setValidations } } = useSuperGridValidate({
		validate: validateSpotifyEntity,
		gridApi,
	});
	const [stagedChangeEvents, setStagedChangeEvents] = useState<CellValueChangedEvent[]>([]);
	const [undoEvents, setUndoEvents] = useState<CellValueChangedEvent[]>([]);
	const [savingEvents, setSavingEvents] = useState<CellValueChangedEvent[]>([]);

	useEffect(() => {
		(gridApi?.getColumns() ?? []).forEach(col => {
			col.setColDef(
				col.getColDef(),
				{
					cellRendererParams: params => ({
						isSaving: (savingEvents.some(e => e.node === params.node && e.column === params.column)),
						doUndo:   (undoEvents.some(e => e.node === params.node && e.column === params.column))
							? () => {
								params.node.setDataValue(
									params.column,
									undoEvents.find(e => e.node === params.node && e.column === params.column)?.oldValue,
									'undoChange',
								);
								setUndoEvents(curr => curr.filter(
									e => e.node !== params.node || e.column !== params.column,
								));
							} : undefined,
					}),
				},
				'api',
			);
		});
	}, [undoEvents, savingEvents]);

	const doUpdates = async (events: CellValueChangedEvent[]) => {
		const validEvents: CellValueChangedEvent[] = [];
		const invalidEvents: CellValueChangedEvent[] = [];
		setSavingEvents(events);

		// eslint-disable-next-line no-restricted-syntax
		for (const e of events) {
			// eslint-disable-next-line no-await-in-loop
			const preValidations = await validateSpotifyEntity({
				changeEvent:      e,
				level:            e?.data?.level as SpotifyLevel,
				validationErrors: [],
			});

			if (preValidations.length > 0) {
				setValidations(
					curr => [
						...curr.filter(
							validation => validation.id !== e.data?.id && validation.level === e.data?.level,
						),
						...preValidations.filter(validation => !!validation.id) as SuperGridValidationEntity[],
					],
				);

				invalidEvents.push(e);
			} else {
				setValidations(curr => curr.filter(
					validation => validation.id !== e.data?.id && validation.level === e.data?.level,
				));
				validEvents.push(e);
			}
		}

		setStagedChangeEvents(invalidEvents);
		setSavingEvents(validEvents);

		if (validEvents.length === 0) {
			return;
		}

		const variables: {
			campaignChanges: Array<UpdateCampaignInput & { id: string }>,
			groupChanges: Array<UpdateGroupInput & { id: string }>,
			adChanges: Array<UpdateAdInput & { id: string }>,
			creativeChanges: Array<UpdateCreativeInput & { id: string }>,
		} = validEvents.reduce((accu, e) => {
			const changeName = e.data.level;
			const newMutation = {
				id:                            e.data.id,
				[`${changeName}Hash`]:         e.data.id,
				[LEVEL_ID_FILTER[changeName]]: e.data.id,
				[e.colDef.field ?? '']:        e.newValue,
			};

			accu[`${changeName}Changes`] = [
				...(accu[`${changeName}Changes`] ?? []),
				getUpdateMutationVariables(newMutation, e.data.level, e.data),
			];

			return accu;
		}, {
			campaignChanges: [],
			groupChanges:    [],
			adChanges:       [],
			creativeChanges: [],
		});

		const { data, errors } = await client.mutate({
			mutation:  UpdateEntitiesDocument,
			variables: {
				campaignChanges: mergeVariablesByIds<UpdateCampaignInput>(variables.campaignChanges),
				groupChanges:    mergeVariablesByIds<UpdateGroupInput>(variables.groupChanges),
				adChanges:       mergeVariablesByIds<UpdateAdInput>(variables.adChanges),
			},
			errorPolicy: 'all',
		});

		const castErrors = (errors ?? []) as unknown as GraphQLErrorWithFormValidation[];

		const validationErrors = [
			...mapValidationErrors(
				castErrors,
			),
			...getAndMapGeneralErrors(errors ?? []),
		];

		if (validationErrors.length > 0) {
			setTimeout(() => {
				setValidations(validationErrors);
			});
		}

		const successfulEvents = getSuccessfulEvents(validEvents, [
			...(data?.campaignUpdates ?? []),
			...(data?.groupUpdates ?? []),
			...(data?.adUpdates ?? []),
		]);

		const successfulUndoEvents = successfulEvents?.filter(e => e.source === 'undoChange');
		const successfulUpdateEvents = successfulEvents?.filter(e => e.source !== 'undoChange');

		if (successfulUndoEvents.length > 1) {
			alephToast(
				({ closeToast }) => (
					<NotificationToast type={NotificationType.Change}
						title={	<div className="flex-1">
							<span className="font-medium">
								{successfulUndoEvents.length ?? 0} Changes
							</span> were successfully undone
						</div>}
						closeToast={closeToast}
					/>

				),
				{
					position: 'bottom-right',
				},
			);
		}

		if (successfulUpdateEvents.length > 1) {
			alephToast(
				({ closeToast }) => (
					<NotificationToast type={NotificationType.Change}
						title={<div className="flex-1">
							<span className="font-medium">
								{successfulUpdateEvents?.length ?? 0} Changes
							</span> were successfully made
						</div>}
						action={<button
							className="text-[12px] text-wise-blue-300 hover:text-wise-blue flex gap-[8px] items-center"
							onClick={
								() => {
									closeToast?.();
									validEvents.forEach(e => {
										e.node.setDataValue(e.column, e.oldValue, 'undoChange');
									});
									setUndoEvents([]);
								}
							}
						>
							<UndoIcon
								height={12}
								width={12}
							/> Undo
						</button>}
						closeToast={closeToast}
					/>
				),
				{
					position: 'bottom-right',
				},
			);
		}

		await Promise.all((successfulEvents ?? []).map(async (event: CellValueChangedEvent): Promise<void> => {
			try {
				event?.api?.applyServerSideTransaction({
					update: await transformSpotifyRows([{
						...event.node?.data ?? {},
						...event.data ?? {},
					}], event.node.data.level),
					route: (event.node.getRoute() ?? []).slice(0, -1),
				});
			} catch (e) {
				captureException(e);
			}

			event?.api?.flashCells({
				columns:  [event.column],
				rowNodes: [event.node],
			});
		}));

		setUndoEvents(curr => [...curr, ...successfulUpdateEvents]);
		setSavingEvents([]);

		setTimeout(() => {
			setUndoEvents(curr => curr.filter(e => !successfulUpdateEvents.includes(e)));
		}, 7500);
	};

	const debouncedDoUpdate = useMemo(() => debounce(doUpdates, 100), []);

	useEffect(() => {
		if (stagedChangeEvents.length > 0) {
			debouncedDoUpdate(stagedChangeEvents);
		}
	}, [JSON.stringify(stagedChangeEvents.map(e => e.newValue))]);

	const onCellValueChanged = async (e: CellValueChangedEvent) => {
		setStagedChangeEvents(curr => [
			...curr.filter(
				event => event.rowIndex !== e.rowIndex || event.colDef?.field !== e.colDef?.field,
			),
			e,
		]);
	};

	return {
		onCellValueChanged,
	};
};

export default useSpotifyEditPlugin;
